import { Reducer } from "redux";

import {
    BLOGS_IMAGE_DETAIL_DELETE_IMAGE_FAILURE,
    BLOGS_IMAGE_DETAIL_DELETE_IMAGE_REQUEST,
    BLOGS_IMAGE_DETAIL_DELETE_IMAGE_SUCCESS,
    BLOGS_IMAGE_DETAIL_FETCH_FAILURE,
    BLOGS_IMAGE_DETAIL_FETCH_REQUEST,
    BLOGS_IMAGE_DETAIL_FETCH_SUCCESS,
    BLOGS_IMAGE_DETAIL_ROTATE_IMAGE_FAILURE,
    BLOGS_IMAGE_DETAIL_ROTATE_IMAGE_REQUEST,
    BLOGS_IMAGE_DETAIL_ROTATE_IMAGE_SUCCESS,
    BLOGS_IMAGE_DETAIL_TITLE_EDIT,
    BlogsImageDetailDeleteImageFailureAction,
    BlogsImageDetailDeleteImageRequestAction,
    BlogsImageDetailDeleteImageSuccessAction,
    BlogsImageDetailFetchFailureAction,
    BlogsImageDetailFetchRequestAction,
    BlogsImageDetailFetchSuccessAction,
    BlogsImageDetailRotateImageFailureAction,
    BlogsImageDetailRotateImageRequestAction,
    BlogsImageDetailRotateImageSuccessAction,
    BlogsImageDetailTitleEditAction,
} from "@/mk2/apps/blogs/containers/BlogsImageDetail/BlogsImageDetail.actions";
import { tupdate } from "@/mk2/helpers/types";
import { errorMessage } from "@/mk2/reducers/errorMessage";
import { LoadingState, loadingState } from "@/mk2/reducers/loadingState";

export interface BlogsImageDetailImageState {
    actionLoadingState: LoadingState;
    commentsLoadingState: LoadingState;
    errorMessage: string;
    isEditingTitle: boolean;
    isTiedToPost: boolean;
    loadingState: LoadingState;
}

export const initialBlogsImageDetailImageState: BlogsImageDetailImageState = {
    actionLoadingState: LoadingState.INIT,
    commentsLoadingState: LoadingState.INIT,
    errorMessage: null,
    isEditingTitle: false,
    isTiedToPost: false,
    loadingState: LoadingState.INIT,
};

export interface BlogsImageDetailPostState {
    imageDetails: {
        [imageId: number]: BlogsImageDetailImageState;
    };
    defaultCanAdd: boolean;
}

export const initialBlogsImageDetailPostState: BlogsImageDetailPostState = {
    imageDetails: {},
    defaultCanAdd: true,
};

export interface BlogsImageDetailPagesState {
    [userNameAndPostSlug: string]: BlogsImageDetailPostState;
}

declare type BlogsImageDetailReducerAction =
    | BlogsImageDetailDeleteImageRequestAction
    | BlogsImageDetailDeleteImageSuccessAction
    | BlogsImageDetailDeleteImageFailureAction
    | BlogsImageDetailFetchRequestAction
    | BlogsImageDetailFetchSuccessAction
    | BlogsImageDetailFetchFailureAction
    | BlogsImageDetailRotateImageRequestAction
    | BlogsImageDetailRotateImageSuccessAction
    | BlogsImageDetailRotateImageFailureAction
    | BlogsImageDetailTitleEditAction;

const stateKey = (action: BlogsImageDetailReducerAction) => `${action.username}/${action.postSlug}`;

export const blogsImageDetailsReducer: Reducer<BlogsImageDetailPagesState> = (
    state: BlogsImageDetailPagesState = {},
    action: BlogsImageDetailReducerAction,
) => {
    switch (action.type) {
        case BLOGS_IMAGE_DETAIL_DELETE_IMAGE_REQUEST:
        case BLOGS_IMAGE_DETAIL_DELETE_IMAGE_SUCCESS:
        case BLOGS_IMAGE_DETAIL_DELETE_IMAGE_FAILURE:
        case BLOGS_IMAGE_DETAIL_ROTATE_IMAGE_REQUEST:
        case BLOGS_IMAGE_DETAIL_ROTATE_IMAGE_SUCCESS:
        case BLOGS_IMAGE_DETAIL_ROTATE_IMAGE_FAILURE: {
            const key = stateKey(action);
            const imageId = action.imageId;
            const postState = state[key] || initialBlogsImageDetailPostState;
            const imageState = postState.imageDetails[imageId] || initialBlogsImageDetailImageState;

            return tupdate(state, {
                [key]: tupdate(postState, {
                    imageDetails: tupdate(postState.imageDetails, {
                        [imageId]: tupdate(imageState, {
                            actionLoadingState: loadingState(undefined, action),
                        }),
                    }),
                }),
            });
        }

        case BLOGS_IMAGE_DETAIL_TITLE_EDIT: {
            const key = stateKey(action);
            const imageId = action.imageId;
            const postState = state[key] || initialBlogsImageDetailPostState;
            const imageState = postState.imageDetails[imageId] || initialBlogsImageDetailImageState;

            return tupdate(state, {
                [key]: tupdate(postState, {
                    imageDetails: tupdate(postState.imageDetails, {
                        [imageId]: tupdate(imageState, {
                            isEditingTitle: action.enabled,
                        }),
                    }),
                }),
            });
        }

        case BLOGS_IMAGE_DETAIL_FETCH_REQUEST:
        case BLOGS_IMAGE_DETAIL_FETCH_FAILURE: {
            const key = stateKey(action);
            const imageId = action.imageId;
            const postState = state[key] || initialBlogsImageDetailPostState;
            const imageState = postState.imageDetails[imageId] || initialBlogsImageDetailImageState;

            return tupdate(state, {
                [key]: tupdate(postState, {
                    imageDetails: tupdate(postState.imageDetails, {
                        [imageId]: tupdate(imageState, {
                            errorMessage: errorMessage(undefined, action),
                            loadingState: loadingState(undefined, action),
                            commentsLoadingState: loadingState(undefined, action),
                        }),
                    }),
                }),
            });
        }
        case BLOGS_IMAGE_DETAIL_FETCH_SUCCESS: {
            const key = stateKey(action);
            const imageId = action.imageId;
            const postState = state[key] || initialBlogsImageDetailPostState;
            const imageState = postState.imageDetails[imageId] || initialBlogsImageDetailImageState;

            return tupdate(state, {
                [key]: tupdate(postState, {
                    imageDetails: tupdate(postState.imageDetails, {
                        [imageId]: tupdate(imageState, {
                            errorMessage: errorMessage(undefined, action),
                            loadingState: loadingState(undefined, action),
                            commentsLoadingState: loadingState(undefined, action),
                            isTiedToPost: action.isTiedToPost,
                        }),
                    }),
                    defaultCanAdd: action.canAdd,
                }),
            });
        }

        default:
            return state;
    }
};
